import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { ContainsGroup, ItemStat } from '../../lib/types';
import { CONTAINS_GROUP_LABELS } from '../../lib/ingredientTaxonomy';
import { memberPill } from '../../lib/insightStats';
import { MAX_MEMBERS, sortedMembers, triggerSections } from '../../lib/insightTriggers';
import { triggerHaptic } from '../../lib/haptics';
import { displayFoodName } from '../../lib/foodDisplayName';
import { InsetGroup, Pill, PillTone, PRESS, SectionHeader } from './insightsUi';

const CONFIDENCE_PILL: Record<ItemStat['confidence'], { tone: PillTone; label: string }> = {
  high: { tone: 'rose', label: 'High' },
  medium: { tone: 'amber', label: 'Medium' },
  low: { tone: 'slate', label: 'Low confidence' },
};

const MEMBER_TONE: Record<ReturnType<typeof memberPill>, PillTone> = {
  Suspect: 'amber',
  'Likely fine': 'emerald',
  Unclear: 'slate',
};

function displayName(name: string): string {
  const label = CONTAINS_GROUP_LABELS[name as ContainsGroup];
  if (label) return label;
  return displayFoodName(name);
}

export function statSubtitle(s: ItemStat): string {
  const parts = [`${s.timesEaten} ${s.timesEaten === 1 ? 'meal' : 'meals'}`, `${s.lift.toFixed(1)}× your usual`];
  if (s.typicalLagHours !== null) parts.push(`~${s.typicalLagHours}h later`);
  return parts.join(' · ');
}

/** Expand/collapse easing: critically damped feel, re-targetable because it is a plain CSS transition. */
const EXPAND_TRANSITION =
  'transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none';

function GroupRow({ group, expanded, onToggle }: { group: ItemStat; expanded: boolean; onToggle: () => void }) {
  const members = sortedMembers(group);
  const shown = members.slice(0, MAX_MEMBERS);
  const more = Math.max(group.memberTotal ?? members.length, members.length) - shown.length;
  const verdict = memberPill(group);
  const expandable = members.length > 0;
  const panelId = `trigger-group-${group.name.replace(/\W+/g, '-')}`;

  const rowInner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="text-base text-slate-950 dark:text-white">{displayName(group.name)}</div>
        <div className="text-[0.8125rem] leading-snug text-slate-500 dark:text-zinc-400">{statSubtitle(group)}</div>
      </div>
      <Pill tone={MEMBER_TONE[verdict]}>{verdict}</Pill>
      {expandable && (
        <ChevronRight
          aria-hidden="true"
          className={`w-4 h-4 shrink-0 text-slate-400 dark:text-zinc-500 transition-transform duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none ${
            expanded ? 'rotate-90' : ''
          }`}
        />
      )}
    </>
  );

  return (
    <div>
      {expandable ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={panelId}
          className={`flex w-full items-center gap-3 px-4 py-3 text-left ${PRESS}`}
        >
          {rowInner}
        </button>
      ) : (
        <div className="flex w-full items-center gap-3 px-4 py-3">{rowInner}</div>
      )}

      {expandable && (
        <div
          id={panelId}
          aria-hidden={!expanded}
          className={`grid ${EXPAND_TRANSITION} ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        >
          <div className="min-h-0 overflow-hidden">
            <ul className="bg-slate-50 dark:bg-zinc-800/40 border-t border-slate-200 dark:border-zinc-800 divide-y divide-slate-200 dark:divide-zinc-800">
              {shown.map((m) => {
                const label = memberPill(m);
                return (
                  <li key={m.name} className="flex items-center gap-3 py-2.5 pl-8 pr-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[0.9375rem] text-slate-900 dark:text-zinc-100">{displayName(m.name)}</div>
                      <div className="text-xs leading-snug text-slate-500 dark:text-zinc-400">{statSubtitle(m)}</div>
                    </div>
                    <Pill tone={MEMBER_TONE[label]}>{label}</Pill>
                  </li>
                );
              })}
              {more > 0 && (
                <li className="py-2.5 pl-8 pr-4 text-xs text-slate-500 dark:text-zinc-400">+{more} more</li>
              )}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function IngredientRow({ stat }: { stat: ItemStat }) {
  const pill = CONFIDENCE_PILL[stat.confidence];
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="text-base text-slate-950 dark:text-white">{displayName(stat.name)}</div>
        <div className="text-[0.8125rem] leading-snug text-slate-500 dark:text-zinc-400">{statSubtitle(stat)}</div>
      </div>
      <Pill tone={pill.tone}>{pill.label}</Pill>
    </div>
  );
}

export interface TriggerListProps {
  components: ItemStat[];
  ingredients: ItemStat[];
}

/**
 * "Main triggers" (groups whose verdict is Suspect, then ingredient leads) and "Food groups" (every
 * other eaten group). Group rows carry a verdict pill and expand in place to their members.
 */
export function TriggerList({ components, ingredients }: TriggerListProps) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // A lead already listed under a suspect group in Main triggers is not repeated standalone.
  const { suspectGroups, otherGroups, leads } = triggerSections(components, ingredients);

  const toggle = (name: string) => {
    triggerHaptic('selection');
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const groupRow = (g: ItemStat) => (
    <GroupRow key={`g-${g.name}`} group={g} expanded={expanded.has(g.name)} onToggle={() => toggle(g.name)} />
  );

  return (
    <>
      {(suspectGroups.length > 0 || leads.length > 0) && (
        <section>
          <SectionHeader>Main triggers</SectionHeader>
          <InsetGroup>
            {suspectGroups.map(groupRow)}
            {leads.map((s) => (
              <IngredientRow key={`i-${s.name}`} stat={s} />
            ))}
          </InsetGroup>
        </section>
      )}
      {otherGroups.length > 0 && (
        <section>
          <SectionHeader>Food groups</SectionHeader>
          <InsetGroup>{otherGroups.map(groupRow)}</InsetGroup>
        </section>
      )}
    </>
  );
}
